import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import Logo from '@/components/Logo';
import { Menu, X } from 'lucide-react';

interface HeaderProps {
  onWatchDemoClick?: () => void;
  onBookSetupClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ 
  onWatchDemoClick,
  onBookSetupClick 
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleWatchDemo = () => {
    setIsMobileMenuOpen(false);
    if (onWatchDemoClick) {
      onWatchDemoClick();
    } else {
      navigate('/');
    }
  };

  const handleBookSetup = () => {
    setIsMobileMenuOpen(false);
    if (onBookSetupClick) {
      onBookSetupClick();
    } else {
      navigate('/custom-solutions');
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-brand-primary/20">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <div className="flex items-center">
            <Logo width="w-12" height="h-12" textSize="text-2xl" linkTo="/" />
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <LanguageSwitcher />
            <button
              onClick={handleWatchDemo}
              className="text-white/90 hover:text-brand-secondary transition-colors duration-200 font-medium"
            >
              {t('institutional.header.watchDemo')}
            </button>
            <Link
              to="/solutions"
              className="text-white/90 hover:text-brand-secondary transition-colors duration-200 font-medium"
            >
              {t('footer.navSolutions')}
            </Link>
            <button
              onClick={handleBookSetup}
              className="relative group bg-gradient-to-r from-brand-primary to-brand-secondary text-white px-6 py-3 rounded-xl font-semibold overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-brand-secondary/50 hover:scale-105"
            >
              <span className="relative z-10">{t('institutional.header.freeConsultation')}</span>
              <div className="absolute inset-0 bg-gradient-to-r from-brand-secondary to-brand-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
          </div>
          <div className="md:hidden flex items-center space-x-3">
            <LanguageSwitcher variant="compact" />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-white/90 hover:text-brand-secondary transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 py-4">
            <div className="flex flex-col space-y-4">
              <button
                onClick={handleWatchDemo}
                className="text-left text-white/90 hover:text-brand-secondary transition-colors duration-200 font-medium py-2"
              >
                {t('institutional.header.watchDemo')}
              </button>
              <Link
                to="/solutions"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-white/90 hover:text-brand-secondary transition-colors duration-200 font-medium py-2"
              >
                {t('footer.navSolutions')}
              </Link>
              <button
                onClick={handleBookSetup}
                className="relative group bg-gradient-to-r from-brand-primary to-brand-secondary text-white px-6 py-3 rounded-xl font-semibold overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-brand-secondary/50 w-full text-center"
              >
                <span className="relative z-10">{t('institutional.header.freeConsultation')}</span>
                <div className="absolute inset-0 bg-gradient-to-r from-brand-secondary to-brand-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;

